import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Instagram, Facebook, ShieldCheck, Award } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const COLUMN_LINKS = [
    {
        title: "Expertise PA",
        links: [
            { to: "/expertise-pa", label: "Les 3 cursus" },
            { to: "/diversification-activite", label: "Diversification d'activité" },
            { to: "/modele-economique", label: "Modèle économique" },
            { to: "/sessions", label: "Prochaines sessions" },
            { to: "/actualite-pa", label: "L'actualité PA" },
        ],
    },
    {
        title: "Découvrir",
        links: [
            { to: "/nos-formateurs", label: "Nos formateurs" },
            { to: "/notre-comite-scientifique", label: "Comité scientifique" },
            { to: "/prise-en-charge", label: "Prise en charge FIF PL / DPC" },
            { to: "/contact", label: "Contact" },
        ],
    },
    {
        title: "Qualité",
        links: [
            { to: "/indicateurs", label: "Indicateurs de performance" },
            { to: "/reclamation", label: "Réclamations" },
            { to: "/accessibilite", label: "Accessibilité & handicap" },
            { to: "/reglement-interieur", label: "Règlement intérieur" },
        ],
    },
];

export default function Footer() {
    return (
        <footer className="bg-brand-ink text-white grain" data-testid="site-footer">
            <div className="mx-auto max-w-7xl px-6 pt-20 pb-10">
                <div className="grid gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                        <img
                            src={LOGO_URL}
                            alt="Cursus Physio"
                            className="h-12 w-auto brightness-0 invert"
                        />
                        <p className="mt-6 max-w-md text-white/70 text-base leading-relaxed">
                            <span className="text-white">L'Institut de la <span className="font-semibold text-brand-orange">Prévention Active</span>.</span>{" "}
                            Cursus Physio forme les kinésithérapeutes à une pratique proactive : prévenir la douleur,
                            la blessure et la perte d'autonomie, au plus haut niveau d'exigence scientifique.
                        </p>

                        <div className="mt-8 flex flex-col gap-3 text-sm">
                            <a
                                href="mailto:contact@cursus-physio.fr"
                                className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                                data-testid="footer-email"
                            >
                                <Mail className="h-4 w-4 text-brand-orange" /> contact@cursus-physio.fr
                            </a>
                            <a
                                href="tel:+33186760000"
                                className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                                data-testid="footer-phone"
                            >
                                <Phone className="h-4 w-4 text-brand-orange" /> +33 1 86 76 00 00
                            </a>
                            <span className="inline-flex items-center gap-3 text-white/80">
                                <MapPin className="h-4 w-4 text-brand-orange" /> 12 rue du Faubourg Saint-Antoine, 75011 Paris
                            </span>
                        </div>

                        <div className="mt-10 flex items-center gap-3">
                            <a href="https://www.linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn"
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 transition-colors"
                                data-testid="footer-social-linkedin">
                                <Linkedin className="h-4 w-4" />
                            </a>
                            <a href="https://www.instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram"
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 transition-colors"
                                data-testid="footer-social-instagram">
                                <Instagram className="h-4 w-4" />
                            </a>
                            <a href="https://www.facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook"
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 transition-colors"
                                data-testid="footer-social-facebook">
                                <Facebook className="h-4 w-4" />
                            </a>
                        </div>
                    </div>

                    {COLUMN_LINKS.map((col) => (
                        <div key={col.title} className="lg:col-span-2">
                            <h4 className="font-display text-base text-white">{col.title}</h4>
                            <ul className="mt-6 space-y-3">
                                {col.links.map((link) => (
                                    <li key={`${col.title}-${link.label}`}>
                                        <Link
                                            to={link.to}
                                            className="text-sm text-white/70 hover:text-white hover:underline underline-offset-4"
                                            data-testid={`footer-link-${col.title.toLowerCase()}-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div className="lg:col-span-2">
                        <h4 className="font-display text-base text-white">Certifications</h4>
                        <div className="mt-6 space-y-4">
                            <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                    <Award className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold">Qualiopi</div>
                                    <div className="text-xs text-white/60">Référencé</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                    <ShieldCheck className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold">DPC / FIF PL</div>
                                    <div className="text-xs text-white/60">Prise en charge</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-white/50 md:flex-row md:items-center md:justify-between">
                    <div>© {new Date().getFullYear()} Cursus Physio · L'Institut de la Prévention Active — Tous droits réservés.</div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <a href="#" className="hover:text-white">Mentions légales</a>
                        <a href="#" className="hover:text-white">CGV</a>
                        <a href="#" className="hover:text-white">Politique de confidentialité</a>
                        <Link to="/accessibilite" className="hover:text-white" data-testid="footer-link-accessibilite">Accessibilité</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
